import React, { useEffect, useState } from 'react';
import { Modal, Box, Slide, IconButton, Avatar } from '@mui/material';
import { Add, ArrowBack } from '@mui/icons-material';
import { getShortName, useJunoCommonFunctions } from '../../../client/Juno/helpers';
import { useSelector } from 'react-redux';
import LogoutIcon from '@mui/icons-material/Logout';
import CustomSwitch from '../common/CustomSwitch';
import useToggleTheme from '../../hooks/useToggleTheme';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import AdditionalAccounts from './AdditionalAccounts';
import PersonalInfo from './PersonalInfo';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import useEmailNotification from '../../hooks/useEmailNotification';
import SecuritySettings from './securitySettings/SecuritySettings';
import useDownloadAccountConfirm from '../../hooks/useDownloadAccountConfirm';
import ProfileImage from './ProfileImage';

const Options = ({ label, index, len, addSwitch, onSwitch, value, onClick }) => {
    return (
        <>
            <div className="flex justify-between w-full" onClick={onClick ? onClick : null}>
                <div className="justify-start !text-text text-sm font-medium !self-center uppercase leading-tight">
                    {label}
                </div>
                {addSwitch ? (
                    <CustomSwitch className="" onChange={onSwitch} checked={value} />
                ) : null}
            </div>
            {index != len - 1 ? <div className="self-stretch h-px relative !bg-secondary" /> : null}
        </>
    );
};

Options.propTypes = {
    label: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    len: PropTypes.number.isRequired,
    addSwitch: PropTypes.bool,
    onSwitch: PropTypes.func,
    value: PropTypes.bool,
};

export const headerLabelMap = {
    'personal-info': 'Personal Information',
    statements: 'Statements',
    'additional-accounts': 'Additional Accounts',
    'download-confirmation': 'Download Account Confirmation',
    'personal-image': 'personal image',
    // Add more as needed
};

export const getHeaderLabel = key => headerLabelMap[key] || 'Profile';

const ProfilePageMobileView = () => {
    const clientData = useSelector(state => state.juno.clientData);
    const [darkMode, setDarkMode] = useToggleTheme();
    const [receiveTransactionEmail, handleGetEmail] = useEmailNotification();
    const [generatePDF] = useDownloadAccountConfirm();
    const { logout } = useJunoCommonFunctions();
    const navigate = useNavigate();
    const [modalFlag, setModalFlag] = useState(false);
    const [mobileSelectedKey, setMobileSelectedKey] = useState(null);
    const handleModal = () => {
        setModalFlag(prev => !prev);
    };
    const { readOnly, accountRole } = clientData || {};

    const modals = [
        { key: 'personal-info', label: 'personal information' },
        { key: 'statements', label: 'statements' },
        { key: 'additional-accounts', label: 'additional accounts' },
        {
            key: 'download-confirmation',
            label: 'download account confirmation',
        },
        // filter out options not visible for additional users
    ].filter((_, index) => ((readOnly || accountRole == 'user') && index == 2 ? false : true));

    const renderModalComponent = key => {
        switch (key) {
            case 'additional-accounts':
                return <AdditionalAccounts modalFlag={modalFlag} setModalFlag={setModalFlag} />;
            case 'personal-info':
                return <PersonalInfo modalFlag={modalFlag} setModalFlag={setModalFlag} />;
            case 'personal-image':
                return (
                    <ProfileImage
                        modalFlag={modalFlag}
                        setModalFlag={setModalFlag}
                        isMobile={true}
                    />
                );
            default:
                return null;
        }
    };

    const HeaderActionIcon = ({ selectedKey, onClick }) => {
        switch (selectedKey) {
            case 'personal-info':
            case 'additional-accounts': {
                return (
                    <div onClick={readOnly && selectedKey == 'personal-info' ? null : onClick}>
                        <div className="p-2 cursor-pointer hover:!bg-card-bg rounded-full">
                            {selectedKey == 'additional-accounts' && (
                                <Add fontSize="medium" className="invert-0 dark:invert" />
                            )}
                            {selectedKey == 'personal-info' && (
                                <CreateOutlinedIcon
                                    fontSize="medium"
                                    className="invert-0 dark:invert"
                                />
                            )}
                        </div>
                    </div>
                );
            }
            default: {
                return null;
            }
        }
    };

    useEffect(() => {
        if (mobileSelectedKey == 'statements') {
            navigate('/client/statements', {
                state: { onBack: '/client/profile' },
            });
        } else if (mobileSelectedKey == 'download-confirmation') {
            generatePDF();
            setMobileSelectedKey(null);
        }
    }, [mobileSelectedKey]);

    const switches = [
        {
            label: 'email notifications',
            addSwitch: true,
            value: receiveTransactionEmail,
            onSwitch: () => handleGetEmail(),
        },
        {
            label: 'two-factor authorisation (2fa)',
            addSwitch: true,
            onSwitch: () => setMobileSelectedKey('2fa'),
            value: clientData?.twoFA?.enabled,
        },
        {
            label: 'dark mode',
            addSwitch: true,
            onSwitch: () => setDarkMode(prev => !prev),
            value: darkMode,
        },
    ].filter((_, index) => (readOnly && index == 1 ? false : true));

    const ConditionalWrap = ({ condition, wrap, children }) =>
        condition ? wrap(children) : children;

    return (
        <>
            <Modal open={true}>
                <Slide direction="left" in={true} mountOnEnter unmountOnExit>
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            right: 0,
                            width: '100%',
                            height: '100%',
                            bgcolor: 'var(--profile-bg)',
                            zIndex: 10,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            outline: 0,
                        }}>
                        <ConditionalWrap
                            condition={mobileSelectedKey == 'personal-image'}
                            wrap={children => (
                                <Slide direction="left" in={true} mountOnEnter unmountOnExit>
                                    <div>{children}</div>
                                </Slide>
                            )}>
                            <Box
                                className={
                                    'flex p-1 relative bg-modal-bg h-12 rounded-b-lg border-t border-b border-black/10'
                                }>
                                <IconButton
                                    onClick={() => {
                                        if (mobileSelectedKey) {
                                            setMobileSelectedKey(null);
                                        } else {
                                            navigate('/client');
                                        }
                                    }}
                                    aria-label="Go back to client page">
                                    <ArrowBack className="!text-text" />
                                </IconButton>
                                <div className="flex text-text m-auto pr-5 self-center text-md font-semibold leading-normal uppercase">
                                    {getHeaderLabel(mobileSelectedKey)}
                                </div>
                                <HeaderActionIcon
                                    selectedKey={mobileSelectedKey}
                                    onClick={handleModal}
                                />
                            </Box>
                            {mobileSelectedKey && mobileSelectedKey != '2fa' ? (
                                <>
                                    <div className="px-2 py-1 !bg-filterPannel-bg h-screen overflow-y-auto mt-1 rounded-md relative">
                                        {renderModalComponent(mobileSelectedKey)}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Box className="self-stretch p-3 bg-modal-bg rounded-md border-t border-b border-black/10 inline-flex justify-start items-center gap-6 mt-1">
                                        <div className="flex-1 flex justify-start items-center gap-3">
                                            <div
                                                className="flex justify-start items-center gap-3"
                                                onClick={() =>
                                                    setMobileSelectedKey('personal-image')
                                                }>
                                                <Avatar
                                                    src={clientData?.profileImage?.url}
                                                    variant="square"
                                                    imgProps={{
                                                        style: {
                                                            objectFit: 'fill',
                                                        },
                                                    }}
                                                    sx={{
                                                        width: '3rem',
                                                        height: '3rem',
                                                        borderRadius: '.5rem',
                                                        outline: '1px solid !important',
                                                        outlineColor: 'var(--color-text)',
                                                    }}>
                                                    <div className="text-center px-3 py-2 justify-center bg-buttons-tx-modal-bg text-container-bg  text-xl font-semibold  leading-normal uppercase">
                                                        {getShortName(clientData?.name)}
                                                    </div>
                                                </Avatar>
                                            </div>
                                            <div className="flex-1 h-12 inline-flex flex-col justify-center items-start gap-1">
                                                <div className="self-stretch justify-start text-text text-sm font-semibold  uppercase leading-tight">
                                                    {clientData?.name || '-'}
                                                </div>
                                                <div className="self-stretch justify-start text-balanceCard-manage text-xs font-medium  uppercase leading-none">
                                                    Account Number:{' '}
                                                    {clientData?.accountNumber || '-'}
                                                </div>
                                            </div>
                                        </div>
                                        <IconButton onClick={() => logout()}>
                                            <LogoutIcon className="text-badge-text-pending" />
                                        </IconButton>
                                    </Box>

                                    <Box className="self-stretch p-3 bg-modal-bg rounded-md border-t border-b border-black/10 inline-flex justify-start items-center gap-3 mt-1 flex-col">
                                        {modals.map((props, index, arr) => (
                                            <Options
                                                index={index}
                                                len={arr.length}
                                                key={props.key}
                                                {...props}
                                                onClick={() => {
                                                    setMobileSelectedKey(props.key);
                                                }}
                                            />
                                        ))}
                                    </Box>

                                    <Box className="self-stretch p-3 bg-modal-bg rounded-md border-t border-b border-black/10 inline-flex justify-start items-center gap-3 mt-1 flex-col">
                                        {switches.map((props, index, arr) => (
                                            <Options
                                                index={index}
                                                len={arr.length}
                                                key={index}
                                                {...props}
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}
                            {/* TODO: implement delete button */}
                            {/* <Box className="flex justify-center">
                            <JunoButton
                                className={'fixed bottom-0 mb-3 !w-[90%]'}
                                type="otherRedText"
                            >
                                delete account
                            </JunoButton>
                        </Box> */}
                        </ConditionalWrap>
                    </Box>
                </Slide>
            </Modal>
            {mobileSelectedKey == '2fa' && (
                <SecuritySettings
                    isMobile={true}
                    onClose={() => {
                        setMobileSelectedKey(null);
                        setModalFlag(false);
                    }}
                />
            )}
        </>
    );
};

export default ProfilePageMobileView;
